import { Component, ElementRef, inject, Input, OnChanges, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IconService } from '@core/services/misc/icon.service';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-icon',
  standalone: true,
  template: '',
  providers: [IconService],
})
export class IconComponent implements OnInit, OnChanges {
  /**
   * Color of the icon.
   * default #BDBDBD (grey)
   * Applied directly to the SVG element.
   *
   * Example: '#BDBDBD' | 'red' | 'rgb(255, 0, 0)' | 'var(--color-primary)'
   */
  @Input() color: string;
  /**
   * Size of the icon in pixels.
   * default 16px
   * Applied directly to the SVG element.
   *
   * Example: '16px'
   */
  @Input() size = '16px';
  /**
   * Size of the icon box in pixels.
   * default is 10px more than the icon size (16px + 10px = 26px)
   *
   * Applied to the svg parent, span element, only if showBorder is true.
   *
   * Example: '26px'
   */
  @Input() boxSize: string;
  /**
   * CSS style object.
   * Pass style object as is.
   *
   * Applied to the svg parent, span element.
   *
   * Example: { 'margin-right': '10px' }
   */
  @Input() cssStyle: Record<string, string>;
  /**
   * CSS margin.
   * Pass margin as string.
   * This is a shortcut for cssStyle as most of the time we only need margin.
   *
   * Applied to the svg parent, span element.
   *
   * Example: 'margin-right: 10px' or 'margin-bottom: 5px'
   */
  @Input() marginTop: string;
  @Input() marginBottom: string;
  @Input() marginRight: string;
  @Input() marginLeft: string;
  /**
   * CSS class name.
   * Pass class name as string.
   *
   * Applied to the svg , span element.
   *
   * Example: 'margin-right-10' or 'mr-5'
   */
  @Input() cssClass: string;
  /**
   * Show border around the icon.
   *
   * Default false
   * Applied to the svg parent, span element.
   *
   * Example: true
   */
  @Input() showBorder = false;
  /**
   * Background color of the icon.
   * default transparent
   * Applied to the svg parent, span element.
   */
  @Input() backgroundColor: string;
  /**
   * Set given size of icon to span.
   */
  @Input() planeIcon = false;
  @Input() fill: string;
  @Input() isSpinner = false;
  private readonly _el = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _iconService = inject(IconService);
  private _spanElement: HTMLElement;
  private _iconName: string;

  /**
   * Name of the icon file without extension.
   * Only SVGs are supported.
   *
   * Available icons are in assets/icons folder.
   *
   * Icons Names List:
   *  - action-button
   *  - add-event
   *  - calendar
   *  - chevron-down
   *  - chevron-right
   *  - chevron-up
   *  - delete
   *  - delete-icon
   *  - edit
   *  - feature-locked
   *  - location
   *  - message
   *  - news
   *  - pencil
   *  - send
   *  - share
   *  - task
   *  - team
   *  - team-remove
   *  - users
   *  - vertical-ellipsis
   *  - watch
   *
   */
  @Input()
  get iconName(): string {
    if (!this._iconName) {
      throw new Error('Attribute "iconName" is required');
    }
    return this._iconName;
  }

  set iconName(value: string) {
    this._iconName = value;
    this._loadIcon();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['color'] && !changes['color'].firstChange) {
      this._updateIconColor();
    }
  }

  ngOnInit(): void {
    this._initSpanElement();
    if (this._iconName) {
      this._loadIcon();
    }
  }

  private _loadIcon(): void {
    if (!this._iconName || !this._spanElement) {
      return;
    }
    this._iconService
      .get(this._iconName)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          if (data) {
            this._renderer.setProperty(this._spanElement, 'innerHTML', data);
            const svgElement = this._spanElement.querySelector('svg');

            this._renderer.addClass(svgElement, 'app-svg-icon');

            // to center the icon vertically and horizontally inside of span
            this._renderer.setStyle(svgElement, 'position', 'absolute');
            this._renderer.setStyle(svgElement, 'top', '50%');
            this._renderer.setStyle(svgElement, 'left', '50%');
            this._renderer.setStyle(svgElement, 'transform', 'translate(-50%, -50%)');

            if (this.isSpinner) {
              this._renderer.setAttribute(svgElement, 'stroke', this.color ? this.color : 'var(--white)');
            }

            if (this.color) {
              const pathElements = svgElement.querySelectorAll('path, circle, ellipse, rect, polygon');
              pathElements.forEach((pathElement) => {
                // If the path has a stroke, set the stroke color to match the path color
                if (pathElement.getAttribute('stroke')) {
                  this._renderer.setAttribute(pathElement, 'stroke', this.color);
                } else {
                  this._renderer.setAttribute(pathElement, 'fill', this.color);
                  if (this.fill) {
                    this._renderer.setAttribute(pathElement, 'fill', this.fill);
                  }
                }
              });
            }

            // If showBorder is true, set the border color to match the path color
            if (this.showBorder) {
              this._renderer.setStyle(this._spanElement, 'padding', '2px');
              this._renderer.setStyle(this._spanElement, 'border-radius', '5px');
              this._renderer.setStyle(this._spanElement, 'border', `1px solid ${this.color ? this.color : '#BDBDBD'}`);
            }

            if (this.backgroundColor) {
              this._renderer.setStyle(this._spanElement, 'background-color', this.backgroundColor);
              this._renderer.setStyle(this._spanElement, 'border-radius', '5px');
            }

            if (this.size) {
              this._renderer.setAttribute(svgElement, 'width', this.size);
              this._renderer.setAttribute(svgElement, 'height', this.size);
            }

            if (this.boxSize) {
              if (this.showBorder) {
                this._renderer.setStyle(this._spanElement, 'height', this.boxSize);
                this._renderer.setStyle(this._spanElement, 'width', this.boxSize);
              }
            }

            if (this.cssStyle) {
              Object.keys(this.cssStyle).forEach((key) => {
                this._renderer.setStyle(this._spanElement, key, this.cssStyle[key]);
              });
            }

            if (this.marginTop) {
              this._renderer.setStyle(this._spanElement, 'margin-top', this.marginTop);
            }
            if (this.marginBottom) {
              this._renderer.setStyle(this._spanElement, 'margin-bottom', this.marginBottom);
            }
            if (this.marginRight) {
              this._renderer.setStyle(this._spanElement, 'margin-right', this.marginRight);
            }
            if (this.marginLeft) {
              this._renderer.setStyle(this._spanElement, 'margin-left', this.marginLeft);
            }

            if (this.cssClass) {
              this._renderer.setAttribute(this._spanElement, 'class', this.cssClass);
            }
          }
        },
        error: (err) => {
          console.error(`Error loading icon: ${this._iconName}`, err);
        },
      });
  }

  private _initSpanElement(): void {
    if (!this._iconName) {
      return;
    }

    this._spanElement = this._renderer.createElement('span');
    this._renderer.appendChild(this._el.nativeElement, this._spanElement);
    this._renderer.setStyle(this._spanElement, 'display', 'inline-block');
    this._renderer.setStyle(this._spanElement, 'height', this.planeIcon ? this.size : this.size ? `${parseInt(this.size, 10)}px` : '23px');
    this._renderer.setStyle(this._spanElement, 'width', this.planeIcon ? this.size : this.size ? `${parseInt(this.size, 10)}px` : '23px');
    this._renderer.setStyle(this._spanElement, 'position', 'relative');
  }

  private _updateIconColor(): void {
    const svgElement = this._spanElement.querySelector('svg');
    if (svgElement && this.color) {
      const elements = svgElement.querySelectorAll('path, circle, ellipse, rect, polygon');
      elements.forEach((element) => {
        if (element.getAttribute('stroke')) {
          this._renderer.setAttribute(element, 'stroke', this.color);
        } else {
          this._renderer.setAttribute(element, 'fill', this.color);
        }
      });
    }
  }
}
